import React, { Suspense, useContext, useEffect, useRef, useCallback, useState } from 'react'
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom'
import { useDispatch as useReduxDispatch } from 'react-redux'
import { ToastContainer, toast } from 'react-toastify'
import { GlobalContext } from './context'
import { decryptedData } from './views/Access'
import brandLogo from './assets/brand/logo_icon.png'
import './Styles/loading.css'
import { getAllMenus, getRoleMenuByKpost, buildPrivilegeMap } from './services/RoleMenu'
import { installUnauthorizedInterceptor, setUnauthorizedHandler } from './services/apiClient'
import { isTokenExpired, getStoredToken, msUntilExpiry } from './Utils/authUtils'
import { clearStorage, readRawStorage, readStorage } from './Utils/storage'


const loading = (
  <div className="kams-boot">
    <img className="kams-boot__logo" src={brandLogo} alt="KAMS" />
    <div className="kams-boot__bar" role="progressbar" aria-label="Loading">
      <span />
    </div>
  </div>
)

const DefaultLayout = React.lazy(() => import('./layout/DefaultLayout'))
const Login = React.lazy(() => import('./views/pages/login/Login'))
const Page404 = React.lazy(() => import('./views/pages/page404/Page404'))
const Page500 = React.lazy(() => import('./views/pages/page500/Page500'))

const AppRoutes = () => {
  const { state, dispatch } = useContext(GlobalContext)
  const reduxDispatch = useReduxDispatch()
  const { isAuthenticated } = state
  const logoutTimerRef = useRef(null)
  // Starts false so we render the loader (not the route tree) until the async
  // session restore in fetchData() resolves. Prevents the unauthenticated
  // catch-all from redirecting to /login before auth state is known, which
  // would otherwise bounce the user to /dashboard after refresh.
  const [authChecked, setAuthChecked] = useState(false)

  const parseJwt = (token) => {
    try {
      return JSON.parse(atob(token.split('.')[1]))
    } catch (e) {
      return null
    }
  }

  // Central logout: clears both stores + localStorage, then shows an optional
  // toast. Cancels any pending proactive-expiry timer before acting.
  const doLogout = useCallback((message) => {
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current)
      logoutTimerRef.current = null
    }
    clearStorage()
    dispatch({ type: 'LOGOUT' })          // GlobalContext → isAuthenticated: false
    reduxDispatch({ type: 'auth/logout' }) // Redux store
    if (message) toast.error(message)
  }, [dispatch, reduxDispatch])

  // Set (or reset) the timer that fires exactly when the JWT expires.
  // Max safe setTimeout value caps at ~24.8 days to avoid integer overflow.
  const scheduleProactiveLogout = useCallback((token) => {
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current)
      logoutTimerRef.current = null
    }
    const ms = msUntilExpiry(token)
    if (ms <= 0) return
    const delay = Math.min(ms, 2 ** 31 - 1)
    logoutTimerRef.current = setTimeout(() => {
      doLogout('Your session has expired. Please login again.')
    }, delay)
  }, [doLogout])

  const fetchData = async () => {
    const accessToken = getStoredToken()
    const userData = readRawStorage('USERDATA')
    const userToken = readRawStorage('USERTOKEN')

    // No session tokens present → silently ensure logged-out state.
    if (!accessToken && (!userData || !userToken)) {
      dispatch({ type: 'LOGOUT' })
      reduxDispatch({ type: 'auth/logout' })
      return
    }

    // Validate the primary auth token (the one sent with every API call).
    if (accessToken && isTokenExpired(accessToken)) {
      doLogout('Session has expired. Please login again.')
      return
    }

    // Token is valid — arm the proactive expiry timer.
    if (accessToken) {
      scheduleProactiveLogout(accessToken)
    }

    // Restore GlobalContext profile from the encrypted USERDATA / USERTOKEN pair.
    if (userData && userToken) {
      const decryptuserData = decryptedData(userData)
      const decryptuserToken = decryptedData(userToken)
      const decodedJwt = parseJwt(decryptuserToken)
      if (decodedJwt?.exp * 1000 < Date.now()) {
        doLogout('Session has expired. Please login again.')
        return
      }

      // Restore GlobalContext profile (legacy screens depend on this).
      dispatch({
        type: "PROFILE",
        payload: { ...decryptuserData, userToken: decryptuserToken },
      })

      // Restore user profile into Redux so privilege-aware components
      // (AppSidebar, PrivilegeGuard, usePrivilege) read from the store.
      reduxDispatch({
        type: 'auth/setProfile',
        designation:   decryptuserData?.UserProfile?.designation || decryptuserData?.userProfile?.designation || '',
        memberTypes:   decryptuserData?.memberType || decryptuserData?.createdBy || '',
        userId:        decryptuserData?._id || '',
        firstName:     decryptuserData?.firstName || '',
        lastName:      decryptuserData?.lastName || '',
        officialEmail: decryptuserData?.email || '',
      })

      // Restore role-menu privileges into both stores so the sidebar and
      // route guards work correctly after a page refresh without re-login.
      try {
        const kpostId = readStorage('kpostID')
        if (kpostId) {
          const [allMenus, roleData] = await Promise.all([
            getAllMenus(),
            getRoleMenuByKpost(kpostId),
          ])
          const privilegeMap = buildPrivilegeMap(allMenus, roleData)
          reduxDispatch({ type: 'auth/setPrivileges', menuPrivileges: privilegeMap }) // Redux
          dispatch({ type: 'SET_PRIVILEGES', payload: privilegeMap })                // GlobalContext
        }
      } catch (e) {
        console.error('Failed to restore menu privileges:', e)
      }
    }
  }

  // Register the API-layer 401 / auth-error-body handler.
  // Any fetch that receives a 401 or a body-level auth error message will
  // call doLogout, which clears state and shows the Login page immediately.
  useEffect(() => {
    installUnauthorizedInterceptor()
    setUnauthorizedHandler(() => {
      doLogout('Session expired. Please login again.')
    })
    return () => setUnauthorizedHandler(null)
  }, [doLogout])

  // Cross-tab sync: when the user logs out (or the token is removed) in
  // another browser tab, localStorage fires a "storage" event in every
  // other tab. Mirror that logout here so no tab stays authenticated.
  useEffect(() => {
    const handleStorageChange = (e) => {
      // e.key === null  →  localStorage.clear() was called in another tab
      // e.newValue === null on the accessToken key  →  token was removed
      if (e.key === null || (e.key === 'accessToken' && e.newValue === null)) {
        dispatch({ type: 'LOGOUT' })
        reduxDispatch({ type: 'auth/logout' })
      }
    }
    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [dispatch, reduxDispatch])

  // Clean up the proactive logout timer when this component unmounts.
  useEffect(() => {
    return () => {
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current)
    }
  }, [])

  useEffect(() => {
    fetchData().finally(() => setAuthChecked(true))
  }, [])

  return (
    <>
      <ToastContainer />
      <Router>
        <Suspense fallback={loading}>
          {!authChecked ? (
            loading
          ) : isAuthenticated ? (
            <Routes>
              {/* Redirect /login to /dashboard when authenticated */}
              <Route path="/login" element={<Navigate to="/dashboard" replace />} />
              {/* Render DefaultLayout for main app routes */}
              <Route path="/*" element={<DefaultLayout />} />
            </Routes>
          ) : (
            <Routes>
              <Route path="/" element={<Login />} />
              <Route path="/login" element={<Login />} />
              <Route path="/404" element={<Page404 />} />
              <Route path="/500" element={<Page500 />} />
              {/* Redirect unknown routes to login when unauthenticated */}
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          )}
        </Suspense>
      </Router>
    </>
  )
}

export default AppRoutes
